/**
 * NOS-51: Blog post list with filters and actions.
 * Gated by can_manage_content and website_multipage_enabled.
 */

import { Link } from "react-router-dom"
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useOrgStore } from "@/stores/orgStore"
import { formatDate } from "@/lib/format"
import { useBlogPosts, useDeleteBlogPost, getStatus } from "@/hooks/useBlogPosts"
import { useBlogCategories } from "@/hooks/useBlogCategories"
import { toast } from "sonner"
import { useState } from "react"
import type { BlogPostStatus } from "@/hooks/useBlogPosts"

function StatusBadge({
  isPublished,
  publishedAt,
}: {
  isPublished: boolean
  publishedAt: string | null
}) {
  const status = getStatus(isPublished, publishedAt)
  const variants: Record<string, "default" | "secondary" | "outline"> = {
    draft: "secondary",
    published: "default",
    scheduled: "outline",
  }
  return <Badge variant={variants[status]}>{status}</Badge>
}

export function BlogPage() {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null

  const [statusFilter, setStatusFilter] = useState<BlogPostStatus>("all")
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { posts, loading, error, refresh } = useBlogPosts(orgId, {
    status: statusFilter,
    categoryId: categoryFilter,
  })
  const { categories } = useBlogCategories(orgId)
  const { deleteBlogPost } = useDeleteBlogPost(orgId)

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const ok = await deleteBlogPost(deleteId)
    setDeleting(false)
    setDeleteId(null)
    if (ok) {
      toast.success("Post deleted")
      refresh()
    } else {
      toast.error("Failed to delete post")
    }
  }

  return (
    <PermissionGate permission="can_manage_content" fallback={<Navigate to="/dashboard" replace />}>
      <div className="min-w-0 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Blog</h1>
              <p className="text-muted-foreground">
                Create and manage blog posts for your website.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to="/dashboard/builder?tab=blog">Categories</Link>
              </Button>
              <Button asChild>
                <Link to="/dashboard/blog/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New post
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as BlogPostStatus)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="published">Published</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={categoryFilter ?? "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? null : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : posts.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center">
              <p className="text-muted-foreground">No blog posts yet.</p>
              <Button asChild variant="outline" className="mt-4">
                <Link to="/dashboard/blog/new">Create your first post</Link>
              </Button>
            </div>
          ) : (
            <div className="min-w-0 overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell className="font-medium">
                        <Link
                          to={`/dashboard/blog/${post.id}/edit`}
                          className="hover:underline"
                        >
                          {post.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          isPublished={post.is_published}
                          publishedAt={post.published_at}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {post.category_id
                          ? categories.find((c) => c.id === post.category_id)?.name ?? "—"
                          : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(post.published_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(post.updated_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/dashboard/blog/${post.id}/edit`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(post.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete post?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </PermissionGate>
  )
}
